// src/oura/components/search/TrafficSources.tsx
//
// Where visits come from, which pages they land on, and what they lead to,
// from the Google Analytics insight blocks (ourteam-backend#6426). Each card
// renders only when its block arrived: absent is unmeasured, never a zero.
// The one exception the backend makes on purpose is `aiReferrals: []`, a
// measured "no visit came from an AI assistant".

import { BotIcon, FileTextIcon, SplitIcon } from "lucide-react";
import type { AeoTrafficInsights } from "@service/aeo";
import { FoldCard, TYPE } from "@oura/ui";
import { cn } from "../../lib/cn";
import { TABLE } from "../client/staffScreen";
import { aiName, num } from "./format";
import { Change } from "./PctChange";

/** GA4's default channel groups in words a shop owner uses. Staff read GA4's own. */
const CHANNEL_WORDS: Record<string, string> = {
    "Organic Search": "Google and other search",
    Direct: "Typed in or bookmarked",
    "Organic Social": "Social media",
    Referral: "Other websites",
    "Paid Search": "Search ads",
    "Paid Social": "Social ads",
    Email: "Email",
    "Organic Video": "Video",
    "Organic Shopping": "Shopping",
    Display: "Display ads",
    "Cross-network": "Ads across Google",
    Unassigned: "Not known",
};

/** "▲ 12%", "New", or nothing when the earlier figure was not measured. */
export function Versus({ now, before }: { now: number | null; before: number | null }) {
    if (now === null || before === null) return null;
    if (before === 0) return now > 0 ? <span className="font-medium text-emerald-700">New</span> : null;
    const pct = ((now - before) / before) * 100;
    if (Math.abs(pct) < 1) return <span className="text-gray-500">Steady</span>;
    return <Change up={pct > 0}>{Math.round(Math.abs(pct))}%</Change>;
}

export function SourcesCard({ traffic, isStaff }: { traffic: AeoTrafficInsights; isStaff?: boolean }) {
    // An unmeasured row (null) sorts last, draws no bar and is never "most".
    const sources = [...(traffic.sources ?? [])].sort((a, b) => {
        if (a.sessions === null) return b.sessions === null ? 0 : 1;
        if (b.sessions === null) return -1;
        return b.sessions - a.sessions;
    });
    const ai = [...(traffic.aiReferrals ?? [])].sort((a, b) => (b.sessions ?? 0) - (a.sessions ?? 0));
    const topSource = sources.find((s) => s.sessions !== null && s.sessions > 0);
    const top = Math.max(1, ...sources.flatMap((s) => (s.sessions === null ? [] : [s.sessions])));
    return (
        <FoldCard
            id="visit-sources"
            title="Where visits come from"
            icon={{ icon: SplitIcon, area: "google" }}
            foldedFact={topSource ? `Most from ${isStaff ? topSource.channel : CHANNEL_WORDS[topSource.channel] ?? topSource.channel}` : undefined}
        >
            <p className={cn(TYPE.tc, "m-0 -mt-1 mb-3 text-gray-500")}>
                {isStaff ? "Sessions by default channel group, last 28 days, against the 28 before." : "Visits in the last 28 days, against the 28 before."}
            </p>
            {sources.length > 0 && (
                <div className="flex flex-col gap-2.5">
                    {sources.map((s) => (
                        <div key={s.channel} className="flex flex-col gap-1">
                            <div className={cn(TYPE.tb, "flex justify-between gap-3")}>
                                <span className="min-w-0 truncate font-medium text-gray-900">{isStaff ? s.channel : CHANNEL_WORDS[s.channel] ?? s.channel}</span>
                                <span className="flex shrink-0 gap-2 tabular-nums">
                                    {num(s.sessions)}
                                    <span className={TYPE.tc}><Versus now={s.sessions} before={s.prevSessions} /></span>
                                </span>
                            </div>
                            {s.sessions !== null && (
                                <div className="h-2 rounded-full bg-gray-100">
                                    <div className="h-2 rounded-full bg-blue-500" style={{ width: s.sessions === 0 ? "0%" : `${Math.max(2, (s.sessions / top) * 100)}%` }} />
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            )}
            {traffic.aiReferrals && (
                <div className={cn("flex flex-col gap-1.5", sources.length > 0 && "mt-4 border-t border-gray-100 pt-3")}>
                    <span className={cn(TYPE.tc, "flex items-center gap-1.5 font-semibold text-gray-700")}>
                        <BotIcon aria-hidden size={14} className="text-violet-600" />
                        {isStaff ? "AI referrals" : "Sent by AI assistants"}
                    </span>
                    {ai.length === 0 ? (
                        <span className={cn(TYPE.tc, "text-gray-500")}>No visits from ChatGPT, Perplexity, Gemini or Copilot in the last 28 days.</span>
                    ) : (
                        ai.map((r) => (
                            <div key={r.source} className={cn(TYPE.tb, "flex justify-between gap-3")}>
                                <span className="text-gray-900">{isStaff ? r.source : aiName(r.source)}</span>
                                <span className="flex gap-2 tabular-nums">
                                    {num(r.sessions)}
                                    <span className={TYPE.tc}><Versus now={r.sessions} before={r.prevSessions} /></span>
                                </span>
                            </div>
                        ))
                    )}
                </div>
            )}
        </FoldCard>
    );
}

/** Path only, so the column reads "/private-dining" rather than the whole address. */
function pagePath(page: string): string {
    try {
        const path = new URL(page, "https://x.invalid").pathname || "/";
        return path === "/" ? "Home page" : path;
    } catch {
        return page;
    }
}

export function LandingPagesCard({ pages, isStaff }: { pages: NonNullable<AeoTrafficInsights["landingPages"]>; isStaff?: boolean }) {
    const rows = [...pages].sort((a, b) => (b.sessions ?? 0) - (a.sessions ?? 0)).slice(0, 8);
    return (
        <FoldCard
            id="landing-pages"
            title={isStaff ? "Landing pages" : "Pages that bring customers"}
            icon={{ icon: FileTextIcon, area: "google" }}
            flush
            foldedFact={rows[0] ? `Most visits: ${pagePath(rows[0].page)}` : undefined}
        >
            <table className="w-full table-fixed border-collapse">
                <colgroup>
                    <col />
                    <col className="w-16 sm:w-20" />
                    <col className="w-16 sm:w-20" />
                    <col className="w-20 sm:w-24" />
                </colgroup>
                <thead>
                    <tr>
                        <th className={cn(TABLE.th, "text-left")}>Page</th>
                        <th className={cn(TABLE.th, "text-right")}>{isStaff ? "Sessions" : "Visits"}</th>
                        <th className={cn(TABLE.th, "text-right")}>From AI</th>
                        <th className={cn(TABLE.th, "text-right")}>{isStaff ? "Key events" : "Enquiries"}</th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((p) => (
                        <tr key={p.page}>
                            <td className={cn(TABLE.td, TYPE.tb, "py-2.5 break-words text-gray-900")}>{pagePath(p.page)}</td>
                            <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums")}>{num(p.sessions)}</td>
                            <td className={cn(TABLE.td, TYPE.tb, "text-right tabular-nums text-gray-500")}>{num(p.aiSessions)}</td>
                            <td className={cn(TABLE.td, TYPE.tb, "text-right font-medium tabular-nums text-gray-900")}>{num(p.keyEvents)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </FoldCard>
    );
}
